import { useState } from 'react'
import { ChevronLeft, ChevronRight, Star } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'

interface Testimonial {
  name: string
  company: string
  role: string
  content: string
  rating: number
}

const TestimonialsSection = () => {
  const [currentIndex, setCurrentIndex] = useState(0)

  const testimonials: Testimonial[] = [
    {
      name: 'Maria Silva',
      company: 'TechCorp Soluções',
      role: 'Gerente de RH',
      content: 'O Flow Clock revolucionou nossa gestão de ponto. Conseguimos reduzir em 80% o tempo gasto com processos manuais e aumentamos significativamente a precisão dos registros.',
      rating: 5
    },
    {
      name: 'João Santos',
      company: 'Indústria Brasil S.A.',
      role: 'Diretor de Recursos Humanos',
      content: 'Excelente plataforma! A integração com nosso sistema de folha foi perfeita. O suporte técnico é excepcional e sempre pronto para ajudar.',
      rating: 5
    },
    {
      name: 'Ana Paula Costa',
      company: 'Serviços Prime',
      role: 'Coordenadora de RH',
      content: 'Estamos muito satisfeitos com o Flow Clock. Os relatórios são completos e nos ajudam a tomar decisões estratégicas. Recomendo fortemente!',
      rating: 5
    }
  ]

  const nextTestimonial = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length)
  }

  const prevTestimonial = () => {
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length)
  }

  return (
    <section className="py-20 bg-white dark:bg-gray-900">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="inline-block px-4 py-2 rounded-full bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 text-sm font-semibold mb-4">
            Depoimentos
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-4">
            O que nossos clientes{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-600 to-primary-800">
              dizem
            </span>
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
            Veja como empresas de diversos segmentos estão transformando 
            sua gestão de RH com o Flow Clock.
          </p>
        </motion.div>

        <div className="max-w-4xl mx-auto relative">
          <div className="flex items-center gap-8">
            <button
              onClick={prevTestimonial}
              className="hidden md:flex w-12 h-12 items-center justify-center rounded-full bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 hover:border-primary-600 hover:text-primary-600 transition-all duration-200 hover:scale-110"
              aria-label="Depoimento anterior"
            >
              <ChevronLeft size={24} />
            </button>

            <div className="flex-1">
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentIndex}
                  initial={{ opacity: 0, x: 50 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -50 }}
                  transition={{ duration: 0.3 }}
                  className="bg-gray-50 dark:bg-gray-800 rounded-2xl p-8 md:p-12"
                >
                  <div className="flex gap-1 mb-6">
                    {[...Array(testimonials[currentIndex].rating)].map((_, i) => (
                      <Star key={i} size={20} fill="#f59e0b" color="#f59e0b" />
                    ))}
                  </div>

                  <p className="text-xl text-gray-700 dark:text-gray-300 mb-8 leading-relaxed italic">
                    "{testimonials[currentIndex].content}"
                  </p>

                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-full bg-gradient-primary flex items-center justify-center text-white text-xl font-bold">
                      {testimonials[currentIndex].name.charAt(0)}
                    </div>
                    <div>
                      <p className="font-bold text-gray-900 dark:text-white text-lg">
                        {testimonials[currentIndex].name}
                      </p>
                      <p className="text-gray-600 dark:text-gray-400 text-sm">
                        {testimonials[currentIndex].role}
                      </p>
                      <p className="text-gray-500 dark:text-gray-500 text-sm">
                        {testimonials[currentIndex].company}
                      </p>
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>

            <button
              onClick={nextTestimonial}
              className="hidden md:flex w-12 h-12 items-center justify-center rounded-full bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 hover:border-primary-600 hover:text-primary-600 transition-all duration-200 hover:scale-110"
              aria-label="Próximo depoimento"
            >
              <ChevronRight size={24} />
            </button>
          </div>

          <div className="flex justify-center gap-2 mt-8">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`h-2 rounded-full transition-all duration-200 ${
                  index === currentIndex ? 'w-8 bg-primary-600' : 'w-2 bg-gray-300 dark:bg-gray-700'
                }`}
                aria-label={`Ir para depoimento ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}

export default TestimonialsSection
